import type { NormalizedCacheObject } from '@apollo/client';
import type { Cache } from '@apollo/client/cache';

import { ApolloClient, InMemoryCache } from '@apollo/client';

import { createApolloLinks } from './createApolloLinks';
import { getGraphqlServerUriForClientSide } from '../helpers';

// For ssr and client side usage

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

const isSSR = typeof globalThis.window === 'undefined';

const cache = new InMemoryCache({
  resultCacheMaxSize: 2 ** 14,
  resultCaching: !isSSR,
  typePolicies: {
    User: {
      keyFields: false,
    },
    Subscription: {
      keyFields: false,
    },
    CartMutation: {
      keyFields: false,
    },
    CommonData: {
      keyFields: false,
    },
    Cart: {
      keyFields: false,
    },
    Wishlist: {
      keyFields: false,
    },
    CartCustomer: {
      keyFields: false,
    },
    ProductDynamicData: {
      keyFields: false,
    },
    Recommendations: {
      keyFields: false,
    },
    VoucherApplyResult: {
      keyFields: false,
    },
    ExponeaCustomerAttributesResponse: {
      keyFields: false,
    },
  },
});

// This avoids Apollo’s warning about the deprecated canonizeResults option
const originalDiff = cache.diff.bind(cache);

const removeCanonizeResults = <TData, TVariables>(
  options: Cache.DiffOptions<TData, TVariables>
): Cache.DiffOptions<TData, TVariables> => {
  if (!('canonizeResults' in options)) {
    return options;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { canonizeResults, ...rest } = options as Cache.DiffOptions<TData, TVariables> & {
    canonizeResults?: boolean;
  };

  return rest as Cache.DiffOptions<TData, TVariables>;
};

cache.diff = options => originalDiff(removeCanonizeResults(options));

function createClient() {
  const uri =
    typeof globalThis.window === 'undefined'
      ? process.env.GRAPHQL_ENDPOINT_SERVER_SIDE
      : getGraphqlServerUriForClientSide(
          globalThis.window,
          process.env.GRAPHQL_ENDPOINT_CLIENT_SIDE
        );

  if (!uri) {
    throw new Error('GraphQL endpoint URI is not defined');
  }

  return new ApolloClient<NormalizedCacheObject>({
    // forcefully sets watchQuery fetchPolicy to 'cache-first' when 'network-only' or 'cache-and-network'
    // forcefully sets query fetchPolicy to 'cache-first'
    ssrMode: isSSR,
    link: createApolloLinks(uri, isSSR),
    clientAwareness: {
      name: 'storefront',
      version: '1.0',
    },
    cache,
    defaultOptions: {
      mutate: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        nextFetchPolicy(currentFetchPolicy) {
          if (currentFetchPolicy === 'network-only' || currentFetchPolicy === 'cache-and-network') {
            return 'cache-first';
          }

          return currentFetchPolicy;
        },
        refetchWritePolicy: 'overwrite',
      },
    },
  });
}

function getClient(): ApolloClient<NormalizedCacheObject> {
  // client side we want to reuse
  // the apolloClient instance if present
  if (typeof globalThis.window !== 'undefined') {
    if (!apolloClient) {
      apolloClient = createClient();
    }

    return apolloClient;
  }

  return createClient();
}

export { getClient };
